<script>
import Liveblog from '@/services/Liveblog'
import liveblogFormFields from '@/components/modals/liveblog/liveblogFormFields.vue'
import { buildLiveblogSubmitData } from '@/components/modals/liveblog/liveblogSubmit'
import { resolveTeamIdFromForm } from '@/components/modals/liveblog/liveblogEventPrefill'
import {
    emptyLiveblogForm,
    liveblogEntryToForm,
    getLiveEntryDefaults,
    LIVEBLOG_CONTENT_TYPES,
} from './liveblogFormModel'
import {
    buildQuickEventButtons,
    EXTRA_QUICK_EVENT_CODES,
    FOOTBALL_QUICK_EVENT_CODES,
    resolveEventMetaForForm,
} from './liveblogEventTypeMeta'

export default {
    name: 'LiveblogComposer',
    components: { liveblogFormFields },
    props: {
        matchId: { type: String, default: '' },
        match: { type: Object, default: null },
        eventTypes: { type: Array, default: () => [] },
        periods: { type: Array, default: () => [] },
        eventReasons: { type: Array, default: () => [] },
        matchLineups: { type: Object, default: null },
        entries: { type: Array, default: () => [] },
    },
    data() {
        return {
            mode: 'add',
            editingEventId: '',
            form: emptyLiveblogForm(),
            tryingToSubmit: false,
        }
    },
    computed: {
        isEdit() {
            return this.mode === 'edit'
        },
        matchLabel() {
            if (!this.match) return ''
            return `${this.match.home_team_code} vs ${this.match.away_team_code}`
        },
        scoreboardLabel() {
            const h = this.form.home_goals !== '' && this.form.home_goals != null ? this.form.home_goals : '0'
            const a = this.form.away_goals !== '' && this.form.away_goals != null ? this.form.away_goals : '0'
            const home = this.match?.home_team_code || 'Home'
            const away = this.match?.away_team_code || 'Away'
            return `${home} ${h} - ${a} ${away}`
        },
        activeEventMeta() {
            return resolveEventMetaForForm(this.eventTypes, this.form.event_type_id)
        },
        quickEventButtons() {
            return buildQuickEventButtons(this.eventTypes, FOOTBALL_QUICK_EVENT_CODES)
        },
        extraQuickEventButtons() {
            return buildQuickEventButtons(this.eventTypes, EXTRA_QUICK_EVENT_CODES)
        },
        showTeamPicker() {
            return this.showLiveControls && this.activeEventMeta.requiresTeam
        },
        showScoreboard() {
            return this.showLiveControls && this.activeEventMeta.showScoreboard
        },
        homeTeamCode() {
            return (this.match && this.match.home_team_code) || 'Home'
        },
        awayTeamCode() {
            return (this.match && this.match.away_team_code) || 'Away'
        },
        contentTypes() {
            return LIVEBLOG_CONTENT_TYPES
        },
        showLiveControls() {
            return this.form.content_type === 'event'
        },
    },
    watch: {
        matchId(next, prev) {
            if (next !== prev) {
                this.resetAdd()
            }
        },
    },
    methods: {
        resetAdd(overrides = {}) {
            this.mode = 'add'
            this.editingEventId = ''
            this.form = getLiveEntryDefaults(this.match, this.entries, overrides)
        },
        startAdd(contentType = 'event') {
            this.resetAdd({ content_type: contentType })
        },
        loadForEdit(item) {
            const mapped = liveblogEntryToForm(item)
            if (!mapped) return
            this.mode = 'edit'
            this.editingEventId = item.event_id
            this.form = mapped
        },
        loadPrefill(prefill) {
            this.mode = 'add'
            this.editingEventId = ''
            this.form = { ...getLiveEntryDefaults(this.match, this.entries), ...prefill }
        },
        cancelEdit() {
            this.resetAdd()
        },
        setContentType(type) {
            this.updateField('content_type', type)
        },
        selectTeamSide(side) {
            if (!this.match) return
            const isHome = side === 'home'
            const partial = {
                team_code: (isHome ? this.match.home_team_code : this.match.away_team_code) || '',
                place: side,
                team_id:
                    isHome && this.match.home_team_id != null && this.match.home_team_id !== ''
                        ? String(this.match.home_team_id)
                        : !isHome && this.match.away_team_id != null && this.match.away_team_id !== ''
                          ? String(this.match.away_team_id)
                          : '',
            }
            if (this.$refs.liveblogForm && typeof this.$refs.liveblogForm.syncPlayersForSide === 'function') {
                this.form = this.$refs.liveblogForm.syncPlayersForSide(partial)
                return
            }
            this.form = { ...this.form, ...partial }
        },
        applyQuickEvent(eventTypeId) {
            const match = this.eventTypes.find((et) => String(et.event_type_id) === String(eventTypeId))
            this.form = {
                ...this.form,
                content_type: 'event',
                event_type_id: String(eventTypeId),
                event_type_name: match?.event_type_name || '',
            }
        },
        updateField(key, val) {
            this.form = { ...this.form, [key]: val }
        },
        bumpScore(side) {
            const key = side === 'home' ? 'home_goals' : 'away_goals'
            const current = Number(this.form[key])
            const next = Number.isFinite(current) ? current + 1 : 1
            this.updateField(key, String(next))
        },
        buildPayload() {
            const content_type = this.form.content_type
            const data = {
                ...this.form,
                content_type,
                team_id: resolveTeamIdFromForm(this.form, this.match),
            }
            const payload = buildLiveblogSubmitData(data, { isCreate: !this.isEdit })
            if (payload instanceof FormData) {
                if (!payload.has('content_type') && content_type) {
                    payload.append('content_type', content_type)
                }
                return payload
            }
            return { ...payload, content_type }
        },
        stripHtml(text) {
            if (!text) return ''
            const el = document.createElement('div')
            el.innerHTML = String(text)
            return (el.textContent || el.innerText || '').trim()
        },
        validate() {
            if (this.form.content_type === 'text') {
                if (!this.stripHtml(this.form.title)) return 'Title is required'
                if (!this.stripHtml(this.form.body)) return 'Body is required'
            }
            if (this.form.content_type === 'event' && !this.form.event_type_id) {
                return 'Event type is required'
            }
            if (
                this.form.content_type === 'image' &&
                !this.form.image_file &&
                !(this.form.payload && this.form.payload.url)
            ) {
                return 'Provide an image URL or upload a file'
            }
            if (this.form.content_type === 'gallery') {
                if (this.$refs.liveblogForm) this.$refs.liveblogForm.syncGalleryPayload()
                const ready = this.$refs.liveblogForm ? this.$refs.liveblogForm.getGalleryReadyItems() : []
                if (!ready.length) return 'Add at least one gallery image'
            }
            if (
                this.isEdit &&
                this.form.content_type === 'image' &&
                !this.form.image_file &&
                !(this.form.payload && this.form.payload.url)
            ) {
                return 'Provide an image URL or upload a file'
            }
            return null
        },
        async submit(saveAndAnother = false) {
            if (!this.matchId) {
                this.failedmsg('Select a match first')
                return
            }
            const validationError = this.validate()
            if (validationError) {
                this.failedmsg(validationError)
                return
            }
            this.tryingToSubmit = true
            try {
                let response
                if (this.isEdit) {
                    response = await Liveblog.updateLiveblog(
                        this.matchId,
                        this.editingEventId,
                        this.buildPayload()
                    )
                } else {
                    response = await Liveblog.createLiveblog(this.matchId, this.buildPayload())
                }
                if (response.data && !response.data.error) {
                    this.successmsg(this.isEdit ? 'Entry updated' : 'Entry created')
                    this.$emit('saved', { mode: this.mode, saveAndAnother })
                    if (saveAndAnother && !this.isEdit) {
                        this.resetAdd({ content_type: this.form.content_type })
                    } else if (!this.isEdit) {
                        this.resetAdd({ content_type: this.form.content_type })
                    } else {
                        this.resetAdd()
                    }
                } else {
                    this.failedmsg(this.formatError(response.data))
                }
            } catch (error) {
                this.failedmsg(this.formatError(error.response?.data))
            } finally {
                this.tryingToSubmit = false
            }
        },
        formatError(data) {
            if (!data) return 'Request failed'
            if (Array.isArray(data.error)) return data.error.join(', ')
            const message = data.error || 'Request failed'
            if (message === 'event_id already exists') {
                return 'This event already exists on the timeline. For goals, update the score first. For kick-off or cards, check for a duplicate entry.'
            }
            return message
        },
    },
}
</script>

<template>
    <div class="liveblog-composer">
        <div class="liveblog-composer__header">
            <div>
                <h5 class="mb-1">{{ isEdit ? 'Edit entry' : 'Add entry' }}</h5>
                <p v-if="matchLabel" class="text-muted small mb-0">{{ matchLabel }}</p>
            </div>
            <button
                v-if="isEdit"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="cancelEdit"
            >
                Cancel edit
            </button>
        </div>

        <div class="liveblog-composer__types mb-3">
            <span class="text-muted small d-block mb-2">Entry type</span>
            <div class="liveblog-composer__type-buttons">
                <button
                    v-for="type in contentTypes"
                    :key="type.value"
                    type="button"
                    class="btn btn-sm liveblog-composer__type-btn"
                    :class="form.content_type === type.value ? 'btn-primary' : 'btn-outline-primary'"
                    :disabled="isEdit"
                    :title="type.label"
                    @click="setContentType(type.value)"
                >
                    <i class="mdi" :class="type.icon"></i>
                    <span>{{ type.label }}</span>
                </button>
            </div>
            <p v-if="isEdit" class="text-muted small mb-0 mt-2">Entry type cannot be changed while editing.</p>
        </div>

        <div v-if="showLiveControls" class="liveblog-composer__quick mb-3">
            <span class="text-muted small d-block mb-2">Quick event</span>
            <div class="d-flex flex-wrap gap-2 mb-2">
                <button
                    v-for="btn in quickEventButtons"
                    :key="btn.code"
                    type="button"
                    class="btn btn-sm btn-outline-dark liveblog-composer__quick-btn"
                    :class="{ 'btn-dark': String(form.event_type_id) === btn.eventTypeId }"
                    :title="btn.label"
                    @click="applyQuickEvent(btn.eventTypeId)"
                >
                    <i class="mdi" :class="btn.icon.split(' ')[0]"></i>
                    {{ btn.label }}
                </button>
            </div>
            <div v-if="extraQuickEventButtons.length" class="d-flex flex-wrap gap-2 mb-3">
                <button
                    v-for="btn in extraQuickEventButtons"
                    :key="`extra-${btn.code}`"
                    type="button"
                    class="btn btn-sm btn-outline-secondary liveblog-composer__quick-btn"
                    :class="{ 'btn-secondary': String(form.event_type_id) === btn.eventTypeId }"
                    :title="btn.label"
                    @click="applyQuickEvent(btn.eventTypeId)"
                >
                    {{ btn.label }}
                </button>
            </div>
            <p v-if="activeEventMeta.requiresRelatedPlayer" class="text-muted small mb-2">
                Substitution: pick player off and related player on.
            </p>
            <span v-if="showTeamPicker" class="text-muted small d-block mb-2">Team</span>
            <div v-if="showTeamPicker" class="d-flex flex-wrap gap-2 mb-3">
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="form.place === 'home' ? 'btn-dark' : 'btn-outline-dark'"
                    @click="selectTeamSide('home')"
                >
                    {{ homeTeamCode }}
                </button>
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="form.place === 'away' ? 'btn-dark' : 'btn-outline-dark'"
                    @click="selectTeamSide('away')"
                >
                    {{ awayTeamCode }}
                </button>
            </div>
        </div>

        <div v-if="showScoreboard" class="liveblog-composer__scoreboard mb-3">
            <div class="liveblog-composer__scoreboard-label">{{ scoreboardLabel }}</div>
            <div class="d-flex gap-2 mt-2">
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="bumpScore('home')">
                    {{ homeTeamCode }} +1
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="bumpScore('away')">
                    {{ awayTeamCode }} +1
                </button>
            </div>
        </div>

        <liveblogFormFields
            ref="liveblogForm"
            v-model="form"
            :match="match"
            :event-types="eventTypes"
            :periods="periods"
            :event-reasons="eventReasons"
            :match-lineups="matchLineups"
            :is-edit="isEdit"
            :show-common-fields="true"
            :hide-type-selector="true"
        />

        <div class="liveblog-composer__footer">
            <b-button variant="outline-secondary" :disabled="tryingToSubmit" @click="isEdit ? cancelEdit() : resetAdd()">
                Reset
            </b-button>
            <b-button variant="dark" :disabled="tryingToSubmit || !matchId" @click="submit(false)">
                <b-spinner v-if="tryingToSubmit" small class="me-2" />
                {{ isEdit ? 'Update' : 'Save' }}
            </b-button>
            <b-button
                v-if="!isEdit"
                variant="success"
                :disabled="tryingToSubmit || !matchId"
                @click="submit(true)"
            >
                Save &amp; add another
            </b-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.liveblog-composer {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.liveblog-composer__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}
.liveblog-composer__type-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.liveblog-composer__type-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 5.5rem;
    justify-content: center;
    font-weight: 600;
}
.liveblog-composer__type-btn .mdi {
    font-size: 1rem;
    line-height: 1;
}
.liveblog-composer__quick-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
}
.liveblog-composer__quick-btn .mdi {
    font-size: 0.95rem;
    line-height: 1;
}
.liveblog-composer__type-btn:disabled {
    opacity: 1;
}
.liveblog-composer__type-btn.btn-primary:disabled {
    opacity: 1;
}
.liveblog-composer__scoreboard {
    padding: 12px 14px;
    background: #f1f3f7;
    border-radius: 12px;
}
.liveblog-composer__scoreboard-label {
    font-weight: 700;
    font-size: 1.15rem;
    color: #151a2d;
}
.liveblog-composer__footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e8ecf1;
    position: sticky;
    bottom: 0;
    background: #fff;
}
.liveblog-composer >>> .marketing-card {
    padding: 18px;
    margin-bottom: 16px;
}
</style>
